import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../store";
import { fetchTrailUsage } from "../../../store/actions/PlanActions";
import { PiClockCountdownLight, PiWarningLight } from "react-icons/pi";
import Button from "../TailwindComponents/Button";
import { useHistory } from "react-router-dom";

interface UsageEntry {
  used: number;
  limit: number | null;
}

interface TrailUsageData {
  is_trail: boolean;
  usage: Record<string, UsageEntry>;
}

const RESOURCE_LABELS: Record<string, string> = {
  matters: "Matters",
  tables: "Tables",
  data_sources: "Data Sources",
  reviews: "Reviews",
};

interface TrailUsageBannerProps {
  /** Only show usage for a specific resource (e.g. "matters" on the Matters page) */
  filterResource?: string;
  compact?: boolean;
}

const TrailUsageBanner: React.FC<TrailUsageBannerProps> = ({ filterResource, compact = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [data, setData] = useState<TrailUsageData | null>(null);

  useEffect(() => {
    let mounted = true;
    dispatch(fetchTrailUsage()).then((result: any) => {
      if (mounted && result) setData(result);
    });
    return () => { mounted = false; };
  }, [dispatch]);

  if (!data || !data.is_trail) return null;

  const entries = Object.entries(data.usage).filter(([key, val]) => {
    if (filterResource && key !== filterResource) return false;
    return val.limit !== null && val.limit !== undefined;
  });

  if (entries.length === 0) return null;

  const hasReachedLimit = entries.some(([, val]) => val.used >= (val.limit ?? Infinity));

  return (
    <div className={`rounded-lg border ${hasReachedLimit ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : 'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'} px-4 py-3 ${compact ? 'mb-2' : 'mb-4'}`}>
      <div className="flex items-center gap-3 mb-2">
        {hasReachedLimit ? (
          <PiWarningLight className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />
        ) : (
          <PiClockCountdownLight className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
        )}
        <span className={`text-sm font-medium ${hasReachedLimit ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'}`}>
          {hasReachedLimit ? t('Trial limit reached') : t('Trial usage')}
        </span>
        <div className="flex-1" />
        <Button
          variant="tertiary"
          size="small"
          onClick={() => history.push('/account?tab=plans')}
        >
          {t('Upgrade')}
        </Button>
      </div>

      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-4'} gap-3`}>
        {entries.map(([key, val]) => {
          const pct = val.limit ? Math.min(100, Math.round((val.used / val.limit) * 100)) : 0;
          const atLimit = val.used >= (val.limit ?? Infinity);
          const barColor = atLimit ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';

          return (
            <div key={key} className="min-w-0">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-700 dark:text-gray-300 truncate">
                  {t(RESOURCE_LABELS[key] || key)}
                </span>
                <span className={`font-medium ${atLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {val.used}/{val.limit}
                </span>
              </div>
              <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrailUsageBanner;
